import React, {memo} from "react"
import {useTranslation} from "react-i18next";


import FormSection from "../../../components/FormSection";
import {formikPropertyChanged, generateMeasurementKey, groupFractureSubType} from "./index";
import Model23Select from "./Model23Select";
import Model23MeasureField from "./Model23MeasureField";
import CollectionsTabList from "./CollectionsTabList";
import Stack from "@mui/material/Stack";
import MeasurementsList, {renderHeader} from "./MeasurementsList";

const RutschBlockTests = memo(({formik, emptyTest, emptyMeasure, model23Registry, inputDisabled}) => {
    const {t} = useTranslation(['models23', 'enums']);

    const onTestsUpdate = (tests) => {
        formik.setFieldValue('rutschBlockTests', tests);
    }

    const onMeasurementsUpdate = (testIndex, measurements) => {
        formik.setFieldValue(`rutschBlockTests[${testIndex}].measurements`, measurements);
    }

    const createEmptyMeasure = () => {
        return {...emptyMeasure, _key: generateMeasurementKey()}
    }

    const renderTest = (testIndex) => {
        return <RutschBlockTest formik={formik}
                                testIndex={testIndex}
                                model23Registry={model23Registry}
                                createEmptyMeasure={createEmptyMeasure}
                                onMeasurementsUpdate={(measurements) => onMeasurementsUpdate(testIndex, measurements)}
                                inputDisabled={inputDisabled}/>
    }

    return <FormSection title={t('model23Form.rutschBlockTests')}
                        openDefault={false}
                        error={Boolean(formik.errors.rutschBlockTests) && formik.touched.rutschBlockTests}>
        <CollectionsTabList collections={formik.values.rutschBlockTests}
                            renderCollection={renderTest}
                            emptyCollection={emptyTest}
                            onCollectionsUpdate={onTestsUpdate}
                            collectionType="test"
                            inputDisabled={inputDisabled}/>
    </FormSection>
}, (prevProps, nextProps) => formikPropertyChanged(prevProps, nextProps, 'rutschBlockTests') && prevProps.model23Registry === nextProps.model23Registry);

const RutschBlockTest = ({formik, testIndex, createEmptyMeasure, onMeasurementsUpdate, model23Registry, inputDisabled}) => {
    const rtAttributesRegistry = model23Registry.data?.rutschBlockTestAttributes;
    const rtRegistry = model23Registry.data?.rutschBlockTestRegistry;
    const rbCodeRegistry = rtRegistry?.rbCode;
    const fractureTypeRegistry = rtRegistry?.fractureType;
    const fractureSubTypeRegistry = rtRegistry?.fractureSubType;
    const fractureSubTypeByType = groupFractureSubType(fractureSubTypeRegistry);

    const test = formik.values.rutschBlockTests[testIndex];
    const testPrefix = `rutschBlockTests[${testIndex}]`

    const headers = ['rbCode', 'fractureType', 'qualityShear', 'score', 'depth', 'tdh'].map(header => {
        const name = rtAttributesRegistry?.[header]?.name ?? header;
        const info = rtAttributesRegistry?.[header]?.description ?? '';
        return renderHeader(name, info);
    })

    const renderMeasure = (row, index) => {
        const handleFractureSelect = (e) => {
            const fractureType = e.target.value;
            let qualityShear = null;

            const filteredFractureSubTypes = fractureSubTypeByType[fractureType] ?? [];

            if(filteredFractureSubTypes.length === 1){
                qualityShear = filteredFractureSubTypes[0].code;
            }

            const newMeasurement = {
                ...row,
                fractureType: fractureType,
                qualityShear: qualityShear,
            }

            // I have to update the entire measurement to prevent rendering issues with Formik and updates of dependent
            // fields.
            formik.setFieldValue(`${testPrefix}.measurements[${index}]`, newMeasurement);
        }

        return <>
            <Model23Select name={`${testPrefix}.measurements[${index}].rbCode`}
                           label=""
                           labelFn={r => `${r.code} - ${r.description}`}
                           formik={formik}
                           registry={rbCodeRegistry}
                           disabled={inputDisabled}/>
            <Model23Select name={`${testPrefix}.measurements[${index}].fractureType`}
                           label=""
                           labelFn={r => `${r.code} - ${r.description}`}
                           formik={formik}
                           onChange={handleFractureSelect}
                           registry={fractureTypeRegistry}
                           disabled={inputDisabled}/>
            <Model23Select name={`${testPrefix}.measurements[${index}].qualityShear`}
                           label=""
                           labelFn={r => `${r.code} - ${r.description}`}
                           formik={formik}
                           disabled={row.fractureType === null || row.fractureType === undefined || inputDisabled}
                           registry={fractureSubTypeByType[row.fractureType] ?? []}/>
            <Model23MeasureField formik={formik}
                                 label=""
                                 name={`${testPrefix}.measurements[${index}].score`}
                                 unitOfMeasure=""
                                 type="number"
                                 disabled={inputDisabled}/>
            <Model23MeasureField formik={formik}
                                 label=""
                                 name={`${testPrefix}.measurements[${index}].depth`}
                                 unitOfMeasure="cm"
                                 type="number"
                                 disabled={inputDisabled}/>
            <Model23MeasureField formik={formik}
                                 label=""
                                 name={`${testPrefix}.measurements[${index}].tdh`}
                                 unitOfMeasure="cm"
                                 type="number"
                                 disabled={inputDisabled}/>
        </>
    }

    return <Stack spacing={2}>
        <MeasurementsList headers={headers}
                          measurements={test.measurements}
                          createEmptyMeasure={createEmptyMeasure}
                          onMeasurementsUpdate={onMeasurementsUpdate}
                          renderMeasure={renderMeasure}
                          inputDisabled={inputDisabled}/>
    </Stack>
}

export default RutschBlockTests;